// v3.18-1384 GMAIL-004 — Live Gmail message list + detail pane with
// Reply / Reply All / Forward + Trash / Restore / Permanently delete
// action buttons.
import { useEffect, useMemo, useState } from 'react';
import { Loader2, Paperclip, Star, Reply, ReplyAll, Forward,
  Trash2, RotateCcw, X, Archive } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const fmtWhen = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffH = (now - d) / 36e5;
    if (diffH < 24) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  } catch { return iso.slice(0, 16); }
};

export default function MailboxMessageList({
  mailbox, folder, search, onReply, onReplyAll, onForward, onDeleted,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());   // v3.18-1385 bulk
  const toggleSel = (gid) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(gid)) n.delete(gid); else n.add(gid); return n;
  });
  const allSelected = items.length > 0 && selected.size === items.length;
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(items.map((m) => m.gmail_message_id)));

  const runBulk = async (action) => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post('/gmail/messages/bulk',
        { message_ids: Array.from(selected), action });
      toast.success(`${action}: ${data.ok_count} ok, ${data.failed_count} failed`);
      setItems((prev) => prev.filter((m) => !selected.has(m.gmail_message_id)));
      setSelected(new Set()); onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Bulk ${action} failed`);
    } finally { setBusy(false); }
  };

  const toggleFlag = async (m, verb) => {
    try {
      await api.post(`/gmail/messages/${m.gmail_message_id}/${verb}`);
      setItems((prev) => prev.map((x) =>
        x.gmail_message_id === m.gmail_message_id
          ? { ...x,
              is_starred: verb === 'star' ? true : verb === 'unstar' ? false : x.is_starred,
              is_unread:  verb === 'unread' ? true : verb === 'read' ? false : x.is_unread,
              folder:     verb === 'archive' ? 'archive' : x.folder,
              labels:     verb === 'archive' ? (x.labels || []).filter((l)=>l!=='INBOX') : x.labels,
            }
          : x));
      if (verb === 'archive') {
        setItems((prev) => prev.filter((x) => x.gmail_message_id !== m.gmail_message_id));
        onDeleted?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${verb} failed`);
    }
  };

  const removeLocal = (gid) => {
    setItems((prev) => prev.filter((m) => m.gmail_message_id !== gid));
    if (selectedId === gid) { setSelectedId(null); setDetail(null); }
  };
  const doTrash = async (gid) => {
    if (!window.confirm('Move this message to Trash?')) return;
    setBusy(true);
    try {
      await api.post(`/gmail/messages/${gid}/trash`);
      toast.success('Moved to Trash');
      removeLocal(gid); onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Trash failed');
    } finally { setBusy(false); }
  };
  const doRestore = async (gid) => {
    setBusy(true);
    try {
      await api.post(`/gmail/messages/${gid}/restore`);
      toast.success('Restored from Trash');
      removeLocal(gid); onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Restore failed');
    } finally { setBusy(false); }
  };
  const doPermanent = async (gid) => {
    if (!window.confirm('Permanently delete? This cannot be undone.')) return;
    setBusy(true);
    try {
      await api.delete(`/gmail/messages/${gid}`);
      toast.success('Permanently deleted');
      removeLocal(gid); onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally { setBusy(false); }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!mailbox?.id) { setItems([]); return; }
      setLoading(true); setSelectedId(null); setDetail(null);
      try {
        const params = new URLSearchParams({
          mailbox_id: mailbox.id, folder, limit: '50',
        });
        if (search) params.set('search', search);
        const { data } = await api.get(`/gmail/messages?${params.toString()}`);
        if (mounted) setItems(data?.items || []);
      } catch {
        if (mounted) setItems([]);
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [mailbox?.id, folder, search]);

  const openMessage = async (msg) => {
    setSelectedId(msg.gmail_message_id);
    setDetail(null);
    try {
      const { data } = await api.get(
        `/gmail/messages/${msg.gmail_message_id}`);
      setDetail(data?.item || null);
    } catch { setDetail(null); }
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center text-slate-500 text-sm">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading messages…
      </div>
    );
  }
  if (!items.length) {
    return (
      <div className="p-10 text-center text-slate-500 text-sm" data-testid="mailbox-empty-folder">
        <div className="w-14 h-14 rounded-full bg-slate-100 mx-auto flex items-center justify-center mb-3">
          <Paperclip className="w-6 h-6 text-slate-300" />
        </div>
        Nothing here yet.
      </div>
    );
  }

  return (
    <div>
      {selected.size > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 bg-amber-50" data-testid="mailbox-bulk-bar">
          <span className="text-xs font-semibold text-amber-900">{selected.size} selected</span>
          <button disabled={busy} onClick={() => runBulk('archive')} data-testid="mailbox-bulk-archive"
                  className="px-2 py-1 rounded bg-white border border-slate-200 text-[11px] hover:bg-slate-50">Archive</button>
          <button disabled={busy} onClick={() => runBulk('trash')} data-testid="mailbox-bulk-trash"
                  className="px-2 py-1 rounded bg-white border border-slate-200 text-[11px] hover:bg-slate-50">Trash</button>
          <button disabled={busy} onClick={() => runBulk('read')} data-testid="mailbox-bulk-read"
                  className="px-2 py-1 rounded bg-white border border-slate-200 text-[11px] hover:bg-slate-50">Mark read</button>
          <button disabled={busy} onClick={() => runBulk('unread')} data-testid="mailbox-bulk-unread"
                  className="px-2 py-1 rounded bg-white border border-slate-200 text-[11px] hover:bg-slate-50">Mark unread</button>
          <button disabled={busy} onClick={() => runBulk('star')} data-testid="mailbox-bulk-star"
                  className="px-2 py-1 rounded bg-white border border-slate-200 text-[11px] hover:bg-slate-50">Star</button>
          <button disabled={busy} onClick={() => runBulk('unstar')} data-testid="mailbox-bulk-unstar"
                  className="px-2 py-1 rounded bg-white border border-slate-200 text-[11px] hover:bg-slate-50">Unstar</button>
          {folder === 'trash' && (
            <>
              <button disabled={busy} onClick={() => runBulk('restore')} data-testid="mailbox-bulk-restore"
                      className="px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px]">Restore</button>
              <button disabled={busy} onClick={() => runBulk('delete')} data-testid="mailbox-bulk-delete"
                      className="px-2 py-1 rounded bg-red-700 text-white text-[11px]">Delete forever</button>
            </>
          )}
          <button onClick={() => setSelected(new Set())} className="ml-auto text-[11px] text-slate-500 hover:text-slate-800">Clear</button>
        </div>
      )}
    <div className="grid grid-cols-1 lg:grid-cols-2">
      <div className="border-r border-slate-100 divide-y divide-slate-100 max-h-[600px] overflow-y-auto" data-testid="mailbox-message-list">
        <div className="px-3 py-1.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
          <input type="checkbox" checked={allSelected} onChange={toggleAll}
                 data-testid="mailbox-select-all"
                 className="h-3 w-3" />
          <span className="text-[10px] text-slate-500 uppercase tracking-wider">Select all</span>
        </div>
        {items.map((m) => (
          <div
            key={m.gmail_message_id}
            className={
              'flex items-start gap-2 px-4 py-3 hover:bg-slate-50 transition ' +
              (selectedId === m.gmail_message_id ? 'bg-slate-100' : '')
            }
          >
            <input type="checkbox"
                   checked={selected.has(m.gmail_message_id)}
                   onChange={() => toggleSel(m.gmail_message_id)}
                   data-testid={`mailbox-check-${m.gmail_message_id}`}
                   className="mt-1 h-3 w-3" />
            <button
              onClick={() => toggleFlag(m, m.is_starred ? 'unstar' : 'star')}
              data-testid={`mailbox-star-${m.gmail_message_id}`}
              className="mt-0.5"
            >
              <Star className={
                'w-3.5 h-3.5 ' +
                (m.is_starred ? 'fill-amber-400 text-amber-500' : 'text-slate-300')
              }/>
            </button>
            <button
              onClick={() => openMessage(m)}
              data-testid={`mailbox-msg-${m.gmail_message_id}`}
              className="flex-1 text-left"
            >
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm font-semibold text-slate-900 truncate">
                {folder === 'sent' ? `To: ${m.to}` : m.from}
              </div>
              <div className="text-[11px] text-slate-500 whitespace-nowrap">
                {fmtWhen(m.internal_date)}
              </div>
            </div>
            <div className={
              'text-sm truncate ' +
              (m.is_unread ? 'font-bold text-slate-900' : 'text-slate-700')
            }>
              {m.subject || '(no subject)'}
              {m.is_starred && <Star className="w-3 h-3 inline-block ml-1 fill-amber-400 text-amber-500" />}
            </div>
            <div className="text-xs text-slate-500 truncate">
              {m.snippet}
            </div>
            <div className="flex items-center gap-2 mt-1">
              {(m.attachments || []).length > 0 && (
                <span className="inline-flex items-center gap-1 text-[10px] text-slate-500">
                  <Paperclip className="w-3 h-3" /> {m.attachments.length}
                </span>
              )}
              {m.client_id && (
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200"
                  data-testid={`mailbox-msg-matched-${m.gmail_message_id}`}
                >
                  Matched · {m.match_source}
                </span>
              )}
            </div>
            </button>
          </div>
        ))}
      </div>

      <div className="p-4 min-h-[400px]" data-testid="mailbox-detail-pane">
        {!selectedId ? (
          <div className="text-center text-slate-400 text-sm mt-16">
            Select a message to preview it here.
          </div>
        ) : !detail ? (
          <div className="text-center text-slate-500 text-sm mt-16">
            <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm font-bold text-slate-900 flex-1">{detail.subject}</div>
              {folder !== 'drafts' && (
                <div className="flex items-center gap-1 shrink-0 flex-wrap" data-testid="mailbox-detail-actions">
                  <button
                    onClick={() => onReply?.(detail)}
                    data-testid="mailbox-detail-reply"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-900 text-white text-[11px] hover:bg-black"
                  ><Reply className="w-3 h-3" /> Reply</button>
                  <button
                    onClick={() => onReplyAll?.(detail)}
                    data-testid="mailbox-detail-reply-all"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-100 text-slate-800 text-[11px] hover:bg-slate-200 border border-slate-200"
                  ><ReplyAll className="w-3 h-3" /> Reply all</button>
                  <button
                    onClick={() => onForward?.(detail)}
                    data-testid="mailbox-detail-forward"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-100 text-slate-800 text-[11px] hover:bg-slate-200 border border-slate-200"
                  ><Forward className="w-3 h-3" /> Forward</button>
                  {folder !== 'trash' ? (
                    <>
                      <button
                        onClick={() => toggleFlag(detail, 'archive')}
                        disabled={busy}
                        data-testid="mailbox-detail-archive"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-100 text-slate-800 text-[11px] hover:bg-slate-200 border border-slate-200"
                      ><Archive className="w-3 h-3" /> Archive</button>
                      <button
                        onClick={() => toggleFlag(detail, detail.is_unread ? 'read' : 'unread')}
                        data-testid="mailbox-detail-read-toggle"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-100 text-slate-800 text-[11px] hover:bg-slate-200 border border-slate-200"
                      >{detail.is_unread ? 'Mark read' : 'Mark unread'}</button>
                      <button
                        onClick={() => doTrash(detail.gmail_message_id)}
                        disabled={busy}
                        data-testid="mailbox-detail-trash"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-50 text-red-700 text-[11px] hover:bg-red-100 border border-red-200"
                      ><Trash2 className="w-3 h-3" /> Trash</button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => doRestore(detail.gmail_message_id)}
                        disabled={busy}
                        data-testid="mailbox-detail-restore"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 text-[11px] hover:bg-emerald-100 border border-emerald-200"
                      ><RotateCcw className="w-3 h-3" /> Restore</button>
                      <button
                        onClick={() => doPermanent(detail.gmail_message_id)}
                        disabled={busy}
                        data-testid="mailbox-detail-permanent-delete"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-700 text-white text-[11px] hover:bg-red-800"
                      ><X className="w-3 h-3" /> Delete forever</button>
                    </>
                  )}
                </div>
              )}
              {folder === 'drafts' && (
                <div className="flex items-center gap-1 shrink-0" data-testid="mailbox-detail-actions">
                  <button
                    onClick={() => doTrash(detail.gmail_message_id)}
                    disabled={busy}
                    data-testid="mailbox-detail-trash"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-50 text-red-700 text-[11px] hover:bg-red-100 border border-red-200"
                  ><Trash2 className="w-3 h-3" /> Discard draft</button>
                </div>
              )}
            </div>
            <div className="text-xs text-slate-500">
              <div><b>From:</b> {detail.from}</div>
              <div><b>To:</b> {detail.to}</div>
              {detail.cc && <div><b>Cc:</b> {detail.cc}</div>}
              <div>{fmtWhen(detail.internal_date)} · thread <span className="font-mono">{detail.thread_id?.slice(0, 12)}…</span></div>
            </div>
            {(detail.attachments || []).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {detail.attachments.map((a) => (
                  <span
                    key={a.attachment_id}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-100 text-xs text-slate-700"
                  >
                    <Paperclip className="w-3 h-3" /> {a.filename}
                    <span className="text-slate-400">({Math.max(1, Math.round((a.size || 0) / 1024))}KB)</span>
                  </span>
                ))}
              </div>
            )}
            {detail.body_html ? (
              <div
                className="prose prose-sm max-w-none border-t border-slate-100 pt-3 text-slate-800"
                dangerouslySetInnerHTML={{ __html: detail.body_html }}
              />
            ) : (
              <div className="border-t border-slate-100 pt-3 text-sm text-slate-800 whitespace-pre-wrap">
                {detail.body_text}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    </div>
  );
}
